import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera as CameraIcon, Trash2, X } from 'lucide-react';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Button } from '@shared/ui/primitives/Button';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { Stepper } from '@shared/ui/lists/Stepper';
import {
  ActionButton,
  FormActions,
  FormSection,
  FormStack,
  SelectField,
  TextInputField,
} from '@shared/ui/forms/FormFields';
import { pageCopy } from '@shared/lib/pageCopy';
import { platform } from '@shared/lib/platform';
import { toast } from '@shared/ui/feedback/Toast';
import { focusNextOnEnter, applyEnterKeyHint } from '@shared/lib/formNav';
import { fetchProfile, type MemberProfile } from '@features/profile/api';
import { fetchMyProperties, type MemberProperty } from '@features/properties/api';
import {
  createPet,
  deletePet,
  fetchActivePetTypes,
  fetchMemberPetFieldPolicy,
  fetchMemberPets,
  updatePet,
  uploadPetPhoto,
  type PetFieldPolicy,
} from './api';

type PetStep = 'details' | 'profile' | 'review';
type PetFieldKey =
  | 'name'
  | 'pet_type_id'
  | 'member_id'
  | 'property_location_id'
  | 'breed'
  | 'colour'
  | 'gender'
  | 'photo_url';

const DEFAULT_PET_FIELD_POLICIES: PetFieldPolicy[] = [
  makeDefaultPolicy('name', 'Name', 'string', true),
  makeDefaultPolicy('pet_type_id', 'Pet type', 'fk', true),
  makeDefaultPolicy('member_id', 'Member', 'fk'),
  makeDefaultPolicy('property_location_id', 'Property', 'fk'),
  makeDefaultPolicy('breed', 'Breed', 'string'),
  makeDefaultPolicy('colour', 'Colour', 'string'),
  makeDefaultPolicy('gender', 'Gender', 'string'),
  makeDefaultPolicy('photo_url', 'Photo', 'string'),
];

const PET_GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'unknown', label: 'Unknown' },
];

const PET_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const PET_PHOTO_TARGET_BYTES = 4.5 * 1024 * 1024;
const PET_PHOTO_MAX_DIMENSION = 1600;

/**
 * Add / edit a pet — the fields shown here follow tenant-configured pet field
 * policy so the resident flow matches admin settings and backend validation.
 */
export function PetForm(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { petId } = useParams<{ petId?: string }>();
  const isEdit = Boolean(petId);
  const formCopy = pageCopy().pets.form;
  const copy = isEdit ? formCopy.edit : formCopy.new;

  const profileQuery = useQuery({ queryKey: ['profile'], queryFn: fetchProfile });
  const memberId = profileQuery.data?.member_id ?? profileQuery.data?.memberId;

  const petPolicyQuery = useQuery({
    queryKey: ['pets', 'policy', memberId],
    queryFn: () => fetchMemberPetFieldPolicy(memberId!),
    enabled: !!memberId,
  });

  const petTypePolicy = getPetFieldPolicy(petPolicyQuery.data, 'pet_type_id');
  const namePolicy = getPetFieldPolicy(petPolicyQuery.data, 'name');
  const memberPolicy = getPetFieldPolicy(petPolicyQuery.data, 'member_id');
  const propertyPolicy = getPetFieldPolicy(petPolicyQuery.data, 'property_location_id');
  const breedPolicy = getPetFieldPolicy(petPolicyQuery.data, 'breed');
  const colourPolicy = getPetFieldPolicy(petPolicyQuery.data, 'colour');
  const genderPolicy = getPetFieldPolicy(petPolicyQuery.data, 'gender');
  const photoPolicy = getPetFieldPolicy(petPolicyQuery.data, 'photo_url');

  const showMemberField = shouldRenderField(memberPolicy);
  const showPropertyField = shouldRenderField(propertyPolicy);
  const showBreedField = shouldRenderField(breedPolicy);
  const showColourField = shouldRenderField(colourPolicy);
  const showGenderField = shouldRenderField(genderPolicy);
  const showPhotoField = shouldRenderField(photoPolicy);

  const petTypesQuery = useQuery({ queryKey: ['pet-types-active'], queryFn: fetchActivePetTypes });
  const propertiesQuery = useQuery({
    queryKey: ['my-properties'],
    queryFn: fetchMyProperties,
    enabled: !!memberId && showPropertyField,
  });

  const petsQuery = useQuery({
    queryKey: ['pets', memberId],
    queryFn: () => fetchMemberPets(memberId!),
    enabled: !!memberId && isEdit,
  });
  const editingPet = isEdit ? petsQuery.data?.find((p) => p.pet_id === petId) : undefined;

  const [petTypeId, setPetTypeId] = useState('');
  const [name, setName] = useState('');
  const [breed, setBreed] = useState('');
  const [colour, setColour] = useState('');
  const [gender, setGender] = useState('');
  const [propertyLocationId, setPropertyLocationId] = useState('');
  const [photo, setPhoto] = useState<{ blob: Blob; preview: string } | null>(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [step, setStep] = useState<PetStep>('details');

  useEffect(() => {
    if (!isEdit || hydrated || !editingPet) return;
    setPetTypeId(editingPet.pet_type_id ?? '');
    setName(editingPet.name ?? '');
    setBreed(editingPet.breed ?? '');
    setColour(editingPet.colour ?? '');
    setGender(editingPet.gender ?? '');
    setPropertyLocationId(editingPet.property_location_id ?? '');
    setExistingPhotoUrl(editingPet.photo_url ?? null);
    setStep('details');
    setHydrated(true);
  }, [isEdit, hydrated, editingPet]);

  useEffect(() => {
    if (isEdit || propertyLocationId || !showPropertyField) return;
    const onlyProperty = propertiesQuery.data?.length === 1 ? propertiesQuery.data[0] : null;
    if (onlyProperty?.property_id) {
      setPropertyLocationId(onlyProperty.property_id);
    }
  }, [isEdit, propertyLocationId, propertiesQuery.data, showPropertyField]);

  useEffect(() => {
    if (!showPhotoField && step === 'profile') {
      setStep('review');
    }
  }, [showPhotoField, step]);

  useEffect(() => {
    return () => {
      if (photo?.preview) URL.revokeObjectURL(photo.preview);
    };
  }, [photo]);

  const steps = showPhotoField ? ['Details', 'Photo', 'Review'] : ['Details', 'Review'];
  const currentStep =
    step === 'details'
      ? 0
      : step === 'profile'
        ? Math.max(steps.indexOf('Photo'), 0)
        : steps.length - 1;

  const previewUrl = photo?.preview ?? existingPhotoUrl;
  const selectedProperty =
    propertiesQuery.data?.find((property) => property.property_id === propertyLocationId) ?? null;
  const memberLabel = describeMember(profileQuery.data);

  const save = useMutation({
    mutationFn: async () => {
      if (!memberId) throw new Error('No member profile found.');
      const payload = {
        pet_type_id: petTypeId,
        name: name.trim(),
        breed: breed.trim(),
        colour: colour.trim(),
        gender: gender.trim(),
        property_location_id: propertyLocationId || null,
      };
      const saved =
        isEdit && petId
          ? await updatePet(memberId, petId, payload)
          : await createPet(memberId, payload);
      if (photo) {
        try {
          await uploadPetPhoto(memberId, saved.pet_id, photo.blob);
        } catch (error) {
          if (!isEdit && photoPolicy.is_required) {
            try {
              await deletePet(memberId, saved.pet_id);
            } catch {
              // Best-effort rollback only; surface the upload error below.
            }
          }
          throw error;
        }
      }
      return saved;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pets', memberId] });
      toast.success(isEdit ? 'Pet updated' : 'Pet added');
      navigate('/app/pets', { replace: true });
    },
    onError: (e) => {
      const message = e instanceof Error ? e.message : 'Could not save pet.';
      toast.danger('Could not save pet', message);
    },
  });

  const removePet = useMutation({
    mutationFn: async () => {
      if (!memberId || !petId) throw new Error('Pet could not be removed.');
      await deletePet(memberId, petId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pets', memberId] });
      toast.success('Pet removed');
      navigate('/app/pets', { replace: true });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Could not remove pet.';
      toast.danger('Could not remove pet', message);
    },
  });

  const pickPhoto = async () => {
    if (attaching) return;
    setAttaching(true);
    try {
      let blob: Blob;
      if (platform.isNative()) {
        const shot = await Camera.getPhoto({
          quality: 80,
          resultType: CameraResultType.Uri,
          source: CameraSource.Prompt,
        });
        if (!shot.webPath) return;
        blob = await fetch(shot.webPath).then((r) => r.blob());
      } else {
        const file = await new Promise<File | null>((resolve) => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/*';
          input.onchange = () => resolve(input.files?.[0] ?? null);
          input.click();
        });
        if (!file) return;
        blob = file;
      }
      const preparedPhoto = await compressPetPhoto(blob);
      if (preparedPhoto.size > PET_PHOTO_MAX_BYTES) {
        throw new Error('This photo is still too large after compression. Please choose a smaller image.');
      }
      setPhoto((prev) => {
        if (prev) URL.revokeObjectURL(prev.preview);
        return { blob: preparedPhoto, preview: URL.createObjectURL(preparedPhoto) };
      });
    } catch (e) {
      toast.danger("Couldn't add photo", e instanceof Error ? e.message : undefined);
    } finally {
      setAttaching(false);
    }
  };

  const removePhoto = () => {
    setPhoto((prev) => {
      if (prev) URL.revokeObjectURL(prev.preview);
      return null;
    });
    setExistingPhotoUrl(null);
  };

  const header = (
    <HeroHeader
      backTo="/app/pets"
      backBehavior="parent"
      eyebrow={copy.eyebrow}
      title={copy.title}
      subtitle={copy.subtitle}
      titleClassName="whitespace-pre-line leading-tight"
    />
  );

  if (isEdit && (petsQuery.isLoading || profileQuery.isLoading || petPolicyQuery.isLoading)) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="flex flex-col gap-4 px-5 pt-5">
          <Skeleton className="h-24" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      </div>
    );
  }

  if (!isEdit && (profileQuery.isLoading || petPolicyQuery.isLoading)) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="flex flex-col gap-4 px-5 pt-5">
          <Skeleton className="h-24" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      </div>
    );
  }

  if (petPolicyQuery.isError || profileQuery.isError || petTypesQuery.isError) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5">
          <ErrorState
            title="Couldn't load pet settings"
            description="Please try again. The pet form needs your current tenant rules before it can continue."
            onRetry={() => {
              profileQuery.refetch();
              petPolicyQuery.refetch();
              petTypesQuery.refetch();
            }}
          />
        </div>
      </div>
    );
  }

  if (isEdit && !petsQuery.isLoading && !editingPet) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5">
          <ErrorState title="Pet not found" onRetry={() => navigate('/app/pets')} />
        </div>
      </div>
    );
  }

  const propertyFieldError =
    showPropertyField && propertiesQuery.isError
      ? 'Could not load your approved properties right now.'
      : undefined;
  const propertyHelper = showPropertyField
    ? propertiesQuery.isLoading
      ? 'Loading your approved properties...'
      : !propertiesQuery.data?.length
        ? propertyPolicy.is_required
          ? 'You need an approved property before this pet can be saved.'
          : 'No approved properties found for your profile yet.'
        : 'Choose the property this pet belongs to.'
    : undefined;

  const canSubmit =
    !!memberId &&
    !!petTypeId &&
    !!name.trim() &&
    (!propertyPolicy.is_required || !!propertyLocationId) &&
    (!breedPolicy.is_required || !!breed.trim()) &&
    (!colourPolicy.is_required || !!colour.trim()) &&
    (!genderPolicy.is_required || !!gender.trim()) &&
    (!photoPolicy.is_required || !!previewUrl);
  const canAdvanceDetails =
    !!petTypeId &&
    !!name.trim() &&
    (!propertyPolicy.is_required || !!propertyLocationId) &&
    (!breedPolicy.is_required || !!breed.trim()) &&
    (!colourPolicy.is_required || !!colour.trim()) &&
    (!genderPolicy.is_required || !!gender.trim()) &&
    !(showPropertyField && propertiesQuery.isError);
  const canAdvancePhoto = !photoPolicy.is_required || !!previewUrl;
  const photoStepIndex = Math.max(steps.indexOf('Photo'), 0);
  const maxReachableStep =
    step === 'details'
      ? (
          canAdvanceDetails
            ? (showPhotoField ? (canAdvancePhoto ? steps.length - 1 : photoStepIndex) : steps.length - 1)
            : 0
        )
      : step === 'profile'
        ? (canAdvancePhoto ? steps.length - 1 : currentStep)
        : currentStep;

  const handleStepSelect = (index: number) => {
    if (index === currentStep) return;
    if (index < 0 || index > maxReachableStep) return;

    if (index === 0) {
      setStep('details');
      return;
    }

    if (showPhotoField && index === photoStepIndex) {
      setStep('profile');
      return;
    }

    setStep('review');
  };

  const validateDetailsStep = (): boolean => {
    if (petTypePolicy.is_required && !petTypeId) {
      toast.danger('Check the form', 'Please select a pet type.');
      return false;
    }
    if (namePolicy.is_required && !name.trim()) {
      toast.danger('Check the form', 'Name is required.');
      return false;
    }
    if (propertyPolicy.is_required && !propertyLocationId) {
      toast.danger('Check the form', 'Please choose a property.');
      return false;
    }
    if (breedPolicy.is_required && !breed.trim()) {
      toast.danger('Check the form', 'Breed is required.');
      return false;
    }
    if (colourPolicy.is_required && !colour.trim()) {
      toast.danger('Check the form', 'Colour is required.');
      return false;
    }
    if (genderPolicy.is_required && !gender.trim()) {
      toast.danger('Check the form', 'Gender is required.');
      return false;
    }
    if (showPropertyField && propertiesQuery.isError) {
      toast.danger('Could not continue', 'Your properties could not be loaded. Please try again.');
      return false;
    }
    return true;
  };

  const validatePhotoStep = (): boolean => {
    if (photoPolicy.is_required && !previewUrl) {
      toast.danger('Check the form', 'A photo is required.');
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (step === 'details') {
      if (!validateDetailsStep()) return;
      setStep(showPhotoField ? 'profile' : 'review');
      return;
    }

    if (step === 'profile') {
      if (!validatePhotoStep()) return;
      setStep('review');
    }
  };

  const goBack = () => {
    if (step === 'review') {
      setStep(showPhotoField ? 'profile' : 'details');
      return;
    }
    if (step === 'profile') {
      setStep('details');
    }
  };

  return (
    <div className="min-h-full bg-paper">
      {header}

      <FormStack className="pt-5">
        <div onKeyDown={focusNextOnEnter} onFocus={applyEnterKeyHint} className="flex flex-col gap-5">
          <FormSection className="pb-5" contentClassName="gap-3">
            <div>
              <h2 className="text-label font-semibold text-ink">
                {step === 'details' ? 'Add the pet details' : step === 'profile' ? 'Add a photo' : 'Review pet details'}
              </h2>
              <p className="mt-1 text-caption text-ink-muted">
                {step === 'details'
                  ? 'Capture the main details for this pet in one place before continuing.'
                  : step === 'profile'
                    ? 'Add an optional photo to help the estate identify this pet faster.'
                    : 'Double-check everything before you save this pet.'}
              </p>
            </div>
            <Stepper
              total={steps.length}
              current={currentStep}
              labels={steps}
              onStepSelect={handleStepSelect}
              isStepAccessible={(index) => index <= maxReachableStep}
            />
          </FormSection>

          {step === 'details' ? (
            <>
              <FormSection
                title="Pet details"
                description="These details help the estate identify the right pet in your household."
              >
                {showMemberField ? (
                  <TextInputField
                    label={memberPolicy.label}
                    value={memberLabel}
                    disabled
                    helper="This pet will be linked to your current member profile."
                  />
                ) : null}

                <SelectField
                  label={petTypePolicy.label}
                  value={petTypeId}
                  onChange={(e) => setPetTypeId(e.target.value)}
                  disabled={petTypesQuery.isLoading}
                  required={petTypePolicy.is_required}
                >
                  <option value="">Choose pet type</option>
                  {(petTypesQuery.data ?? []).map((type) => (
                    <option key={type.pet_type_id} value={type.pet_type_id}>
                      {type.name}
                    </option>
                  ))}
                </SelectField>

                <TextInputField
                  label={namePolicy.label}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Bella"
                  autoCapitalize="words"
                  required={namePolicy.is_required}
                />

                {showPropertyField ? (
                  <SelectField
                    label={propertyPolicy.label}
                    value={propertyLocationId}
                    onChange={(e) => setPropertyLocationId(e.target.value)}
                    disabled={propertiesQuery.isLoading || !propertiesQuery.data?.length}
                    required={propertyPolicy.is_required}
                    optional={!propertyPolicy.is_required}
                    helper={propertyHelper}
                    error={propertyFieldError}
                  >
                    <option value="">
                      {propertiesQuery.data?.length ? 'Choose property' : 'No properties available'}
                    </option>
                    {(propertiesQuery.data ?? []).map((property) => (
                      <option key={property.property_id} value={property.property_id}>
                        {formatPropertyLabel(property)}
                      </option>
                    ))}
                  </SelectField>
                ) : null}

                {showBreedField ? (
                  <TextInputField
                    label={breedPolicy.label}
                    value={breed}
                    onChange={(e) => setBreed(e.target.value)}
                    placeholder={breedPolicy.is_required ? 'Enter breed' : 'Optional'}
                    required={breedPolicy.is_required}
                    optional={!breedPolicy.is_required}
                  />
                ) : null}

                {showColourField ? (
                  <TextInputField
                    label={colourPolicy.label}
                    value={colour}
                    onChange={(e) => setColour(e.target.value)}
                    placeholder={colourPolicy.is_required ? 'Enter colour' : 'Optional'}
                    required={colourPolicy.is_required}
                    optional={!colourPolicy.is_required}
                  />
                ) : null}

                {showGenderField ? (
                  <SelectField
                    label={genderPolicy.label}
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    required={genderPolicy.is_required}
                    optional={!genderPolicy.is_required}
                  >
                    <option value="">{genderPolicy.is_required ? 'Choose gender' : 'Optional'}</option>
                    {PET_GENDER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SelectField>
                ) : null}
              </FormSection>

              {isEdit ? (
                <FormSection
                  title="Remove pet"
                  description="Deleting a pet is permanent. This action is only available from the edit screen."
                >
                  <Button
                    type="button"
                    variant="danger"
                    width="full"
                    loading={removePet.isPending}
                    onClick={() => {
                      const petName = name.trim() || 'this pet';
                      if (!window.confirm(`Remove ${petName}? This cannot be undone.`)) return;
                      removePet.mutate();
                    }}
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                    Remove pet
                  </Button>
                </FormSection>
              ) : null}
            </>
          ) : null}

          {step === 'profile' && showPhotoField ? (
            <FormSection
              title="Photo"
              description="A photo helps security and management identify your pet faster."
            >
              {previewUrl ? (
                <div className="flex items-center gap-3 rounded-md border border-hairline-strong bg-elevated px-3.5 py-3">
                  <img src={previewUrl} alt="Pet" className="h-16 w-16 rounded-lg object-cover hairline" />
                  <div className="min-w-0 flex-1">
                    <p className="text-body-sm font-semibold text-ink">Photo selected</p>
                    <button
                      type="button"
                      onClick={() => void pickPhoto()}
                      className="mt-0.5 text-caption text-primary-700"
                    >
                      Change photo
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={removePhoto}
                    aria-label="Remove photo"
                    className="flex h-8 w-8 items-center justify-center rounded-pill bg-sunken text-ink-secondary"
                  >
                    <X className="h-4 w-4" strokeWidth={2} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => void pickPhoto()}
                  disabled={attaching}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-hairline-strong bg-elevated py-3.5 text-body-sm font-medium text-ink-secondary transition-colors disabled:opacity-60"
                >
                  <CameraIcon className="h-4.5 w-4.5" strokeWidth={1.5} />
                  {attaching ? 'Adding…' : 'Add a photo'}
                </button>
              )}
              {photoPolicy.is_required ? (
                <p className="text-caption text-ink-muted">A photo is required for pets on this tenant.</p>
              ) : null}
            </FormSection>
          ) : null}

          {step === 'review' ? (
            <FormSection
              title="Review"
              description="Make sure the details below are correct before saving."
            >
              {showPhotoField ? (
                previewUrl ? (
                  <div className="flex items-center gap-3 rounded-md border border-hairline-strong bg-elevated px-3.5 py-3">
                    <img src={previewUrl} alt="Pet" className="h-16 w-16 rounded-lg object-cover hairline" />
                    <div>
                      <p className="text-body-sm font-semibold text-ink">Photo attached</p>
                      <p className="text-caption text-ink-muted">This photo will be saved with the pet profile.</p>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-hairline-strong bg-elevated px-3.5 py-3 text-caption text-ink-muted">
                    No photo added.
                  </div>
                )
              ) : null}

              <ReviewRow
                label={petTypePolicy.label}
                value={petTypesQuery.data?.find((type) => type.pet_type_id === petTypeId)?.name ?? '-'}
              />
              <ReviewRow label={namePolicy.label} value={name.trim() || '-'} />
              {showMemberField ? <ReviewRow label={memberPolicy.label} value={memberLabel || '-'} /> : null}
              {showPropertyField ? (
                <ReviewRow
                  label={propertyPolicy.label}
                  value={selectedProperty ? formatPropertyLabel(selectedProperty) : '-'}
                />
              ) : null}
              {showBreedField ? <ReviewRow label={breedPolicy.label} value={breed.trim() || '-'} /> : null}
              {showColourField ? <ReviewRow label={colourPolicy.label} value={colour.trim() || '-'} /> : null}
              {showGenderField ? <ReviewRow label={genderPolicy.label} value={gender.trim() || '-'} /> : null}
            </FormSection>
          ) : null}

          <FormActions sticky>
            {step === 'details' ? (
              <ActionButton disabled={!canAdvanceDetails} onClick={goNext}>
                Next
              </ActionButton>
            ) : step === 'profile' ? (
              <div className="grid grid-cols-2 gap-3">
                <Button type="button" variant="secondary" width="full" onClick={goBack}>
                  Back
                </Button>
                <Button type="button" width="full" disabled={!canAdvancePhoto} onClick={goNext}>
                  Next
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Button type="button" variant="secondary" width="full" onClick={goBack}>
                  Back
                </Button>
                <ActionButton
                  disabled={!canSubmit}
                  loading={save.isPending}
                  onClick={() => {
                    if (!validateDetailsStep()) return;
                    if (showPhotoField && !validatePhotoStep()) return;
                    save.mutate();
                  }}
                >
                  {isEdit ? 'Save changes' : 'Add pet'}
                </ActionButton>
              </div>
            )}
          </FormActions>
        </div>
      </FormStack>
    </div>
  );
}

function makeDefaultPolicy(
  field_key: PetFieldKey,
  label: string,
  type: PetFieldPolicy['type'],
  system_required = false,
): PetFieldPolicy {
  return {
    module_key: 'pets',
    entity_key: 'pet',
    field_key,
    label,
    type,
    system_required,
    is_required: system_required,
    is_visible: true,
    default_value: null,
    is_tenant_overridden: false,
  };
}

function getPetFieldPolicy(
  policies: PetFieldPolicy[] | undefined,
  fieldKey: PetFieldKey,
): PetFieldPolicy {
  return (
    policies?.find((policy): policy is PetFieldPolicy => policy.field_key === fieldKey) ??
    DEFAULT_PET_FIELD_POLICIES.find((policy) => policy.field_key === fieldKey)!
  );
}

function shouldRenderField(policy: PetFieldPolicy): boolean {
  return policy.system_required || policy.is_required || policy.is_visible;
}

function describeMember(profile?: MemberProfile): string {
  const preferred = profile?.preferred_name?.trim();
  if (preferred) return preferred;
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  return profile?.email?.trim() || 'Current member';
}

function formatPropertyLabel(property: MemberProperty): string {
  return [property.unit_label, property.address].filter(Boolean).join(' - ');
}

function ReviewRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-hairline-strong bg-elevated px-3.5 py-3">
      <span className="text-caption font-semibold uppercase tracking-[0.12em] text-ink-muted">{label}</span>
      <span className="text-right text-body-sm text-ink">{value}</span>
    </div>
  );
}

async function compressPetPhoto(blob: Blob): Promise<Blob> {
  if (!blob.type.startsWith('image/')) return blob;

  const image = await loadImage(blob);
  let maxDimension = PET_PHOTO_MAX_DIMENSION;
  let quality = 0.82;
  let output = await renderCompressedImage(image, maxDimension, quality);

  if (
    blob.size <= PET_PHOTO_TARGET_BYTES &&
    Math.max(image.naturalWidth, image.naturalHeight) <= PET_PHOTO_MAX_DIMENSION
  ) {
    return blob;
  }

  while (output.size > PET_PHOTO_TARGET_BYTES && quality > 0.45) {
    quality -= 0.1;
    output = await renderCompressedImage(image, maxDimension, quality);
  }

  while (output.size > PET_PHOTO_TARGET_BYTES && maxDimension > 900) {
    maxDimension -= 200;
    quality = Math.min(quality, 0.72);
    output = await renderCompressedImage(image, maxDimension, quality);

    while (output.size > PET_PHOTO_TARGET_BYTES && quality > 0.45) {
      quality -= 0.08;
      output = await renderCompressedImage(image, maxDimension, quality);
    }
  }

  return output.size < blob.size ? output : blob;
}

async function loadImage(blob: Blob): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not read this image.'));
      img.src = objectUrl;
    });
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function renderCompressedImage(
  image: HTMLImageElement,
  maxDimension: number,
  quality: number,
): Promise<Blob> {
  const { width, height } = fitWithinBounds(image.naturalWidth, image.naturalHeight, maxDimension);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not prepare this image for upload.');

  context.drawImage(image, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', quality);
  });

  if (!blob) throw new Error('Could not compress this image.');
  return blob;
}

function fitWithinBounds(
  width: number,
  height: number,
  maxDimension: number,
): { width: number; height: number } {
  if (Math.max(width, height) <= maxDimension) {
    return { width, height };
  }

  const scale = maxDimension / Math.max(width, height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}
